// @owners { team: patients-team }

const DEFAULT_MAX_SIZE = 10;

/**
 * Memoizes recent calls by cache key. Unlike lodash memoize, entries are evicted once
 * the cache exceeds maxSize (LRU) so memory does not grow across checkout sessions.
 */
export const memoizeLatest = <Args, Result>(
  fn: (arg: Args) => Result,
  getCacheKey: (arg: Args) => string,
  maxSize = DEFAULT_MAX_SIZE,
): ((arg: Args) => Result) => {
  const cache = new Map<string, Result>();

  return (arg: Args) => {
    const key = getCacheKey(arg);
    const cached = cache.get(key);
    if (cached !== undefined) {
      cache.delete(key);
      cache.set(key, cached);
      return cached;
    }

    const result = fn(arg);
    cache.set(key, result);

    if (cache.size > maxSize) {
      const oldestKey = cache.keys().next().value;
      if (oldestKey !== undefined) {
        cache.delete(oldestKey);
      }
    }

    return result;
  };
};
